import React, { createContext, useState, useEffect, useCallback, useContext } from 'react';
import eventEmitter, { EVENT_TYPES } from './ContextEvents';
import localApiAdapter from '../services/localApiAdapter';
import i18n from '../../i18n';

const py = (key, opts) => i18n.t(`serviceMessages.payments.${key}`, opts);

export const PaymentsContext = createContext();

export const usePayments = () => {
  const context = useContext(PaymentsContext);
  if (context === undefined) {
    throw new Error(py('hook_outside_provider'));
  }
  return context;
};

const FACTURATION_PAYMENTS = '/facturation/payments';

function normalizePayment(row) {
  if (!row) return row;
  const installments = Array.isArray(row.installments)
    ? row.installments
    : (typeof row.installments === 'string'
      ? (() => { try { return JSON.parse(row.installments); } catch { return []; } })()
      : []);
  const totalAmount = Number(row.totalAmount ?? row.total_amount ?? 0);
  const amountPaid = Number(row.amountPaid ?? row.amount_paid ?? 0);
  const remainingAmount =
    row.remainingAmount != null || row.remaining_amount != null
      ? Number(row.remainingAmount ?? row.remaining_amount)
      : Math.max(0, totalAmount - amountPaid);

  return {
    ...row,
    invoiceId: row.invoiceId ?? row.invoice_id,
    invoiceNumber: row.invoiceNumber ?? row.invoice_number,
    client: row.client ?? row.client_name ?? '',
    totalAmount,
    amountPaid,
    remainingAmount,
    paymentDate: row.paymentDate ?? row.payment_date ?? null,
    paymentMethod: row.paymentMethod ?? row.payment_method ?? null,
    nextDueDate: row.nextDueDate ?? row.next_due_date ?? null,
    installments,
    created_at: row.created_at ?? row.createdAt,
    updated_at: row.updated_at ?? row.updatedAt,
  };
}

function unwrapPaymentsList(response) {
  if (Array.isArray(response)) return response.map(normalizePayment);
  if (Array.isArray(response?.data)) return response.data.map(normalizePayment);
  return [];
}

function unwrapPaymentRow(response) {
  const row = response?.data ?? response;
  return normalizePayment(row);
}

function getNextDueDate(installments) {
  const pendingInstallments = (installments || [])
    .filter((inst) => inst.status === 'pending')
    .sort((a, b) => new Date(a.dueDate || a.due_date) - new Date(b.dueDate || b.due_date));
  return pendingInstallments.length > 0 ? (pendingInstallments[0].dueDate || pendingInstallments[0].due_date) : null;
}

export const PaymentsProvider = ({ children }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPayments = useCallback(async () => {
    try {
      const response = await localApiAdapter.get(FACTURATION_PAYMENTS);
      setPayments(unwrapPaymentsList(response));
    } catch (error) {
      console.error('Error loading facturation payments:', error);
      setPayments([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const refreshData = useCallback(async () => {
    setLoading(true);
    await loadPayments();
  }, [loadPayments]);

  const persistPayment = useCallback(async (id, payload) => {
    const response = await localApiAdapter.put(`${FACTURATION_PAYMENTS}/${id}`, payload);
    const updated = unwrapPaymentRow(response);
    setPayments((prev) => prev.map((p) => (p.id === id ? updated : p)));
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
    return updated;
  }, []);

  const addPaymentInstallment = useCallback(async (paymentId, installment) => {
    const payment = payments.find((p) => p.id === paymentId);
    if (!payment) throw new Error(py('not_found', { id: paymentId }));

    const newInstallment = {
      id: crypto.randomUUID(),
      amount: installment.amount,
      dueDate: installment.dueDate,
      status: 'pending',
      notes: installment.notes || '',
      created_at: new Date().toISOString(),
    };

    const updatedInstallments = [...(payment.installments || []), newInstallment];
    return persistPayment(paymentId, {
      installments: updatedInstallments,
      nextDueDate: getNextDueDate(updatedInstallments),
    });
  }, [payments, persistPayment]);

  const markInstallmentAsPaid = useCallback(async (paymentId, installmentId, paymentDate = new Date().toISOString().split('T')[0]) => {
    const payment = payments.find((p) => p.id === paymentId);
    if (!payment) throw new Error(py('not_found', { id: paymentId }));

    const installmentIndex = (payment.installments || []).findIndex((i) => i.id === installmentId);
    if (installmentIndex === -1) throw new Error(py('installment_not_found', { id: installmentId }));

    const updatedInstallments = [...payment.installments];
    updatedInstallments[installmentIndex] = {
      ...updatedInstallments[installmentIndex],
      status: 'paid',
      paymentDate,
      updated_at: new Date().toISOString(),
    };

    const totalPaid = updatedInstallments.reduce(
      (sum, inst) => (inst.status === 'paid' ? sum + Number(inst.amount || 0) : sum),
      0
    );
    const remainingAmount = Math.max(0, payment.totalAmount - totalPaid);
    let paymentStatus = 'pending';
    if (totalPaid >= payment.totalAmount) paymentStatus = 'paid';
    else if (totalPaid > 0) paymentStatus = 'partial';

    return persistPayment(paymentId, {
      installments: updatedInstallments,
      amountPaid: totalPaid,
      remainingAmount,
      status: paymentStatus,
      nextDueDate: getNextDueDate(updatedInstallments),
      paymentDate,
    });
  }, [payments, persistPayment]);

  const createPayment = useCallback(async (invoiceId, paymentData = {}) => {
    const payload = {
      id: crypto.randomUUID(),
      invoiceId,
      invoiceNumber: paymentData.invoiceNumber ?? paymentData.invoice_number ?? null,
      client: paymentData.client ?? null,
      totalAmount: Number(paymentData.totalAmount ?? paymentData.total_amount ?? 0),
      amountPaid: Number(paymentData.amountPaid ?? paymentData.amount_paid ?? 0),
      remainingAmount: Number(paymentData.remainingAmount ?? paymentData.remaining_amount ?? 0),
      status: paymentData.status || 'pending',
      nextDueDate: paymentData.dueDate ?? paymentData.nextDueDate ?? paymentData.next_due_date ?? null,
      paymentDate: paymentData.paymentDate ?? paymentData.payment_date ?? null,
      paymentMethod: paymentData.method ?? paymentData.paymentMethod ?? paymentData.payment_method ?? null,
      notes: paymentData.notes ?? paymentData.description ?? '',
      installments: paymentData.installments || [],
    };

    if (!payload.remainingAmount && payload.totalAmount) {
      payload.remainingAmount = Math.max(0, payload.totalAmount - payload.amountPaid);
    }

    const response = await localApiAdapter.post(FACTURATION_PAYMENTS, payload);
    const created = unwrapPaymentRow(response);
    setPayments((prev) => [...prev, created]);
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
    return created;
  }, []);

  const updatePayment = useCallback(async (id, updates) => {
    const response = await localApiAdapter.put(`${FACTURATION_PAYMENTS}/${id}`, updates);
    const updated = unwrapPaymentRow(response);
    setPayments((prev) => prev.map((p) => (p.id === id ? updated : p)));
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
    return updated;
  }, []);

  const deletePayment = useCallback(async (id) => {
    await localApiAdapter.delete(`${FACTURATION_PAYMENTS}/${id}`);
    setPayments((prev) => prev.filter((p) => p.id !== id));
    eventEmitter.emit(EVENT_TYPES.DATA_CHANGED);
  }, []);

  const getPaymentStats = useCallback(() => {
    const pendingPayments = payments.filter((p) => p.status === 'pending').length;
    const partialPayments = payments.filter((p) => p.status === 'partial').length;
    const completedPayments = payments.filter((p) => p.status === 'paid').length;
    const totalReceivable = payments.reduce((sum, p) => sum + Number(p.remainingAmount || 0), 0);
    const totalPaid = payments.reduce((sum, p) => sum + Number(p.amountPaid || 0), 0);

    return {
      pendingPayments,
      partialPayments,
      completedPayments,
      totalReceivable,
      totalPaid,
    };
  }, [payments]);

  const getPaymentsByInvoiceId = useCallback(
    (invoiceId) => payments.filter((p) => p.invoiceId === invoiceId),
    [payments]
  );

  const value = {
    payments,
    loading,
    createPayment,
    updatePayment,
    deletePayment,
    addPaymentInstallment,
    markInstallmentAsPaid,
    getPaymentStats,
    getPaymentsByInvoiceId,
    refreshData,
  };

  return (
    <PaymentsContext.Provider value={value}>
      {children}
    </PaymentsContext.Provider>
  );
};
